import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { applicationSelectors } from '../redux/application';
import { JobTableName } from '../redux/application/application.types';
import { FieldVisibility, fieldVisibilityFor } from './fieldVisibility';

/**
 * Die aufgelöste Sichtbarkeit einer Ansicht. Mit Tabellenname vereinigt sie die
 * portalweite Liste mit der Config dieser Tabelle; ohne bleibt es bei der
 * portalweiten.
 *
 * Für Listen mit tausenden Zeilen NICHT in der Zeile aufrufen, sondern einmal in
 * der Elternliste und als Prop hinunterreichen — siehe JobRowList.
 */
export function useFieldVisibility(table?: JobTableName): FieldVisibility {
    const globalHidden = useSelector(applicationSelectors.getHiddenFields);
    // Bewusst getJobTablesConfig und NICHT die Fabrik getJobTableConfig(table):
    // die Fabrik baut bei jeder Store-Benachrichtigung einen frischen
    // createSelector mit leerem Cache, und der Umweg über (state: any) würde
    // zusätzlich die Typisierung wegwerfen. getJobTablesConfig ist ein einzelner
    // memoisierter Selektor, das Nachschlagen kostet eine Zeile.
    const jobTables = useSelector(applicationSelectors.getJobTablesConfig);
    const tableConfig = table === undefined ? undefined : jobTables?.[table];

    // Referenzstabil, solange die Config gleich bleibt — daran hängen die
    // Spalten-useMemos beider Tabellen. Ein Test in useFieldVisibility.test.tsx
    // hält das fest.
    return useMemo(() => fieldVisibilityFor(globalHidden, tableConfig), [globalHidden, tableConfig]);
}

/**
 * Eine Angabe, eine Frage — für OptionalLabel.
 *
 * Liest bewusst NUR die portalweite Liste. Das ist eine Grenze, keine
 * Vereinfachung: OptionalLabel ist eine allgemeine Komponente und weiß nicht, in
 * welcher Tabelle sie steckt. Ein tabellenlokales Ausblenden gehört in die
 * Spalten-Pipeline, nicht an ein Label.
 *
 * `field === undefined` heißt „nicht im Katalog geführt, nie versteckt" — die
 * über 40 bestehenden OptionalLabel-Aufrufe für Ablesedaten bleiben dadurch
 * unberührt, ohne sie anzufassen.
 */
export function useFieldHidden(field: string | undefined): boolean {
    const globalHidden = useSelector(applicationSelectors.getHiddenFields);

    return field !== undefined && globalHidden.has(field);
}
